/**
 * BLRRD Color — RGB colorspace primaries, white points, and transforms.
 *
 * All gamut conversion matrices are 3×3 row-major Float32, **derived at
 * module load** from CIE-xy chromaticity coordinates + Bradford chromatic
 * adaptation. We do NOT hard-code reference numbers from third parties —
 * derived matrices are guaranteed numerically self-consistent (input ×
 * output = I within float precision), which is exactly what the colour-
 * roundtrip CI gate enforces.
 *
 * Working space: **ACEScg** (AP1 primaries, ~D60 ACES whitepoint, linear).
 *   Chosen because: (a) common-denominator for RAW/Log-C/SLog-3 input,
 *   (b) scene-referred linear math matches our Float32 pipeline as-is,
 *   (c) gamut covers Rec.2020 + Display P3 + sRGB without round-trip loss.
 *
 * References (citation graph in docs/COLORSPACE-DESIGN.md):
 *   - SMPTE ST 2065-1 (ACES2065-1) — AP0 primaries
 *   - Academy S-2014-004 (ACEScg)  — AP1 primaries
 *   - Rec. ITU-R BT.709-6 / BT.2020-2 / sRGB IEC 61966-2-1
 *   - Display P3 — Apple Color Sync technical note
 *   - Bradford chromatic adaptation matrix — Lindbloom (1999)
 *
 * Status: Phase 2.C skeleton. Matrices derived; pipeline integration gated
 *   on a deliberate switch-over PR (ACES = behavioural change → fresh
 *   pixel-hash baselines required).
 */

/* ── Chromaticity coordinates (CIE xy) ──────────────────────────────── */

export const PRIMARIES = Object.freeze({
    /* sRGB / Rec.709 share primaries — only the transfer function differs. */
    sRGB:    { r: [0.6400, 0.3300], g: [0.3000, 0.6000], b: [0.1500, 0.0600], w: [0.31270, 0.32900] },
    rec709:  { r: [0.6400, 0.3300], g: [0.3000, 0.6000], b: [0.1500, 0.0600], w: [0.31270, 0.32900] },
    rec2020: { r: [0.7080, 0.2920], g: [0.1700, 0.7970], b: [0.1310, 0.0460], w: [0.31270, 0.32900] },
    p3:      { r: [0.6800, 0.3200], g: [0.2650, 0.6900], b: [0.1500, 0.0600], w: [0.31270, 0.32900] },
    /* ACES AP0 — wider than human vision; archival/interchange. */
    ap0:     { r: [0.7347, 0.2653], g: [0.0000, 1.0000], b: [0.0001,-0.0770], w: [0.32168, 0.33767] },
    /* ACES AP1 — working space (ACEScg). */
    ap1:     { r: [0.7130, 0.2930], g: [0.1650, 0.8300], b: [0.1280, 0.0440], w: [0.32168, 0.33767] },
});

/* Bradford cone-response matrix (Lindbloom). Used to chromatically adapt
 * tristimulus values between source and destination white points. */
const BRADFORD = [
     0.8951000,  0.2664000, -0.1614000,
    -0.7502000,  1.7135000,  0.0367000,
     0.0389000, -0.0685000,  1.0296000,
];
const BRADFORD_INV = [
     0.9869929, -0.1470543,  0.1599627,
     0.4323053,  0.5183603,  0.0492912,
    -0.0085287,  0.0400428,  0.9684867,
];

/* ── Linear-algebra helpers (3×3, row-major) ────────────────────────── */

function mat3Mul(a, b) {
    const out = new Array(9);
    for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++) {
            out[r * 3 + c] =
                a[r * 3 + 0] * b[0 + c] +
                a[r * 3 + 1] * b[3 + c] +
                a[r * 3 + 2] * b[6 + c];
        }
    }
    return out;
}

function mat3Vec(m, v) {
    return [
        m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
        m[3] * v[0] + m[4] * v[1] + m[5] * v[2],
        m[6] * v[0] + m[7] * v[1] + m[8] * v[2],
    ];
}

function mat3Inv(m) {
    const a = m[0], b = m[1], c = m[2];
    const d = m[3], e = m[4], f = m[5];
    const g = m[6], h = m[7], i = m[8];
    const A = e * i - f * h, B = f * g - d * i, C = d * h - e * g;
    const det = a * A + b * B + c * C;
    if (Math.abs(det) < 1e-12) throw new Error('mat3Inv: singular matrix');
    const inv = 1 / det;
    return [
        A * inv,            (c * h - b * i) * inv, (b * f - c * e) * inv,
        B * inv,            (a * i - c * g) * inv, (c * d - a * f) * inv,
        C * inv,            (b * g - a * h) * inv, (a * e - b * d) * inv,
    ];
}

/* xy → XYZ at Y=1. */
function xyToXYZ(xy) {
    const [x, y] = xy;
    return [x / y, 1, (1 - x - y) / y];
}

/* RGB → XYZ matrix from chromaticities + white point. */
function rgbToXYZ(p) {
    const [Xr, Yr, Zr] = xyToXYZ(p.r);
    const [Xg, Yg, Zg] = xyToXYZ(p.g);
    const [Xb, Yb, Zb] = xyToXYZ(p.b);
    const M = [
        Xr, Xg, Xb,
        Yr, Yg, Yb,
        Zr, Zg, Zb,
    ];
    const W = xyToXYZ(p.w);
    const S = mat3Vec(mat3Inv(M), W);
    return [
        S[0] * Xr, S[1] * Xg, S[2] * Xb,
        S[0] * Yr, S[1] * Yg, S[2] * Yb,
        S[0] * Zr, S[1] * Zg, S[2] * Zb,
    ];
}

/* Bradford CAT: src white → dst white, both XYZ-Y=1. */
function bradfordCAT(srcWxy, dstWxy) {
    const Ws = xyToXYZ(srcWxy), Wd = xyToXYZ(dstWxy);
    const cs = mat3Vec(BRADFORD, Ws);
    const cd = mat3Vec(BRADFORD, Wd);
    const D = [
        cd[0] / cs[0], 0, 0,
        0, cd[1] / cs[1], 0,
        0, 0, cd[2] / cs[2],
    ];
    return mat3Mul(BRADFORD_INV, mat3Mul(D, BRADFORD));
}

/* Compose: linear-RGB-src → ACEScg(AP1, D60).
 * Path: src-RGB → XYZ_src → Bradford(src_W → AP1_W) → AP1-RGB. */
function deriveToACEScg(srcName) {
    const srcP = PRIMARIES[srcName];
    const ap1P = PRIMARIES.ap1;
    const M_src_XYZ = rgbToXYZ(srcP);
    const CAT       = bradfordCAT(srcP.w, ap1P.w);
    const M_XYZ_AP1 = mat3Inv(rgbToXYZ(ap1P));
    return new Float32Array(mat3Mul(M_XYZ_AP1, mat3Mul(CAT, M_src_XYZ)));
}

/* Compose: ACEScg → linear-RGB-dst (numerical inverse of deriveToACEScg). */
function deriveFromACEScg(dstName) {
    const dstP = PRIMARIES[dstName];
    const ap1P = PRIMARIES.ap1;
    const M_AP1_XYZ = rgbToXYZ(ap1P);
    const CAT       = bradfordCAT(ap1P.w, dstP.w);
    const M_XYZ_dst = mat3Inv(rgbToXYZ(dstP));
    return new Float32Array(mat3Mul(M_XYZ_dst, mat3Mul(CAT, M_AP1_XYZ)));
}

/* Identity matrix when src/dst share primaries + white point (e.g. AP1 → AP1). */
const IDENTITY3 = new Float32Array([1, 0, 0, 0, 1, 0, 0, 0, 1]);

/* ── Pre-baked 3×3 matrices ─────────────────────────────────────────── */

export const M_sRGB_to_ACEScg     = deriveToACEScg('sRGB');
export const M_rec2020_to_ACEScg  = deriveToACEScg('rec2020');
export const M_p3_to_ACEScg       = deriveToACEScg('p3');
export const M_ap0_to_ACEScg      = deriveToACEScg('ap0');

export const M_ACEScg_to_sRGB     = deriveFromACEScg('sRGB');
export const M_ACEScg_to_rec2020  = deriveFromACEScg('rec2020');
export const M_ACEScg_to_p3       = deriveFromACEScg('p3');
export const M_ACEScg_to_ap0      = deriveFromACEScg('ap0');

/* ── EXR chromaticities-dispatch aliases (Phase 3.C.1) ──────────────────
 * The OpenEXR `chromaticities` attribute declares a file's primaries and
 * whitepoint as 8 CIE-xy floats. We dispatch to one of these four
 * source-→ACEScg matrices after matching the attribute against the
 * reference primary sets. Values are derived (not hard-coded) and
 * Float32-shaped so they can drop straight into the ingest inner loop.
 *
 * AP1 is the working space — `M_ap1_to_ACEScg` is identity by definition
 * (same primaries + whitepoint as ACEScg). We export it as a Float32Array
 * for shape-uniformity with the other three so call-sites don't have to
 * special-case the no-op. */
export const REC709_TO_ACEScg     = M_sRGB_to_ACEScg;     /* sRGB and Rec.709 share primaries */
export const BT2020_TO_ACEScg     = M_rec2020_to_ACEScg;
export const ACES_AP0_TO_ACEScg   = M_ap0_to_ACEScg;
export const ACES_AP1_TO_ACEScg   = new Float32Array([1, 0, 0, 0, 1, 0, 0, 0, 1]);

/**
 * Derive a source-RGB → ACEScg matrix from arbitrary CIE-xy chromaticities.
 * Used by the EXR decoder when the `chromaticities` attribute declares a
 * non-standard primary set (rare — most files match Rec.709 / AP0 / AP1 /
 * BT.2020 within tolerance, in which case the precomputed alias matrices
 * above are used instead).
 *
 * Input shape — same struct as the OpenEXR `chromaticities` attribute:
 *   { redX, redY, greenX, greenY, blueX, blueY, whiteX, whiteY }
 *
 * Returns a Float32Array(9) row-major matrix. Throws if the chromaticities
 * are degenerate (e.g. collinear primaries → singular RGB-to-XYZ matrix).
 *
 * @param {{redX:number,redY:number,greenX:number,greenY:number,
 *          blueX:number,blueY:number,whiteX:number,whiteY:number}} chrom
 * @returns {Float32Array}
 */
export function deriveMatrixFromChromaticities(chrom) {
    const srcP = {
        r: [chrom.redX,   chrom.redY],
        g: [chrom.greenX, chrom.greenY],
        b: [chrom.blueX,  chrom.blueY],
        w: [chrom.whiteX, chrom.whiteY],
    };
    const ap1P       = PRIMARIES.ap1;
    const M_src_XYZ  = rgbToXYZ(srcP);
    const CAT        = bradfordCAT(srcP.w, ap1P.w);
    const M_XYZ_AP1  = mat3Inv(rgbToXYZ(ap1P));
    return new Float32Array(mat3Mul(M_XYZ_AP1, mat3Mul(CAT, M_src_XYZ)));
}

/* ── Transfer functions (encode = linear→display, decode = display→linear)
 * Pure scalar functions; vectorize in `transforms.js`. ────────────────── */

/* sRGB IEC 61966-2-1 (used for sRGB and Display-P3 PNG/JPEG outputs). */
export function decodeSRGB(v) {
    if (v <= 0.04045) return v / 12.92;
    return Math.pow((v + 0.055) / 1.055, 2.4);
}
export function encodeSRGB(v) {
    if (v <= 0.0031308) return 12.92 * v;
    return 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
}

/* Rec.709 / BT.1886 — used for video output, slightly different from sRGB. */
export function decodeRec709(v) {
    if (v < 0.081) return v / 4.5;
    return Math.pow((v + 0.099) / 1.099, 1 / 0.45);
}
export function encodeRec709(v) {
    if (v < 0.018) return 4.5 * v;
    return 1.099 * Math.pow(v, 0.45) - 0.099;
}

/* Rec.2020 — same OETF shape as Rec.709 with tighter coefficients. */
export function decodeRec2020(v) {
    if (v < 0.0812428582986) return v / 4.5;
    return Math.pow((v + 0.0993) / 1.0993, 1 / 0.45);
}
export function encodeRec2020(v) {
    if (v < 0.0181) return 4.5 * v;
    return 1.0993 * Math.pow(v, 0.45) - 0.0993;
}

/* PQ (Rec.2100-PQ / SMPTE ST 2084) — HDR display, normalized [0,1] = 0..10000 nits. */
const PQ_M1 = 2610 / 16384;
const PQ_M2 = 128 * 2523 / 4096;
const PQ_C1 = 3424 / 4096;
const PQ_C2 = 32 * 2413 / 4096;
const PQ_C3 = 32 * 2392 / 4096;
export function encodePQ(v) {
    const Lp = Math.pow(Math.max(0, v), PQ_M1);
    return Math.pow((PQ_C1 + PQ_C2 * Lp) / (1 + PQ_C3 * Lp), PQ_M2);
}
export function decodePQ(v) {
    const Vp = Math.pow(v, 1 / PQ_M2);
    const num = Math.max(0, Vp - PQ_C1);
    const den = PQ_C2 - PQ_C3 * Vp;
    return Math.pow(num / den, 1 / PQ_M1);
}

/* ── Catalog of named output spaces ─────────────────────────────────── */

export const OUTPUT_SPACES = Object.freeze({
    sRGB:        { matrix: M_ACEScg_to_sRGB,    encode: encodeSRGB,    gamut: 'sRGB',    label: 'sRGB' },
    rec709:      { matrix: M_ACEScg_to_sRGB,    encode: encodeRec709,  gamut: 'rec709',  label: 'Rec.709 (Video)' },
    rec2020:     { matrix: M_ACEScg_to_rec2020, encode: encodeRec2020, gamut: 'rec2020', label: 'Rec.2020' },
    rec2020_pq:  { matrix: M_ACEScg_to_rec2020, encode: encodePQ,      gamut: 'rec2020', label: 'Rec.2100-PQ (HDR)' },
    p3:          { matrix: M_ACEScg_to_p3,      encode: encodeSRGB,    gamut: 'p3',      label: 'Display P3' },
    ap0_linear:  { matrix: M_ACEScg_to_ap0,     encode: (v) => v,      gamut: 'ap0',     label: 'ACES2065-1 (linear, EXR)' },
});

export const INPUT_SPACES = Object.freeze({
    sRGB:    { matrix: M_sRGB_to_ACEScg,    decode: decodeSRGB    },
    rec709:  { matrix: M_sRGB_to_ACEScg,    decode: decodeRec709  },
    rec2020: { matrix: M_rec2020_to_ACEScg, decode: decodeRec2020 },
    p3:      { matrix: M_p3_to_ACEScg,      decode: decodeSRGB    },
    ap0:     { matrix: M_ap0_to_ACEScg,     decode: (v) => v      },
    ap1:     { matrix: IDENTITY3,           decode: (v) => v      },  /* identity — already ACEScg */
});
